import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingCart, Search, ChevronDown, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';
export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [shopDropdownOpen, setShopDropdownOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const {
    itemCount,
    openCart
  } = useCart();
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setMobileMenuOpen(false);
    setShopDropdownOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);
  const shopLinks = [{
    name: 'Shop All',
    path: '/shop'
  }, {
    name: 'Robot Lawn Mowers',
    path: '/category/lawn-mowers'
  }, {
    name: 'Robotic Snow Blowers',
    path: '/category/snow-blowers'
  }, {
    name: 'Leaf Blowers',
    path: '/category/leaf-blowers'
  }, {
    name: 'Bundles',
    path: '/category/bundles'
  }, {
    name: 'Modules & Accessories',
    path: '/category/modules'
  }, {
    name: 'Accessories',
    path: '/category/accessories'
  }];
  return <>
      {/* SEO-focused Announcement Bar */}
      <div className="bg-yarbo-dark text-white text-center py-2.5 px-4 text-xs sm:text-sm font-semibold tracking-tight" data-id="element-125">
        <span className="hidden sm:inline" data-id="element-126">
          Shop Yarbo Robot Lawn Mowers, Robotic Snow Blowers, Leaf Blowers &amp;
          Modular Yard Robots
        </span>
        <span className="sm:hidden" data-id="element-127">
          Shop Yarbo Robot Lawn Mowers, Snow Blowers &amp; Yard Robots
        </span>
      </div>

      {/* Main Header */}
      <header className={`sticky top-0 z-50 w-full bg-white transition-all duration-300 ${isScrolled ? 'shadow-md' : 'border-b border-gray-100'}`} data-id="element-128">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-129">
          <div className="flex justify-between items-center h-16 lg:h-20" data-id="element-130">
            {/* Logo */}
            <Link to="/" className="flex flex-col" data-id="element-131">
              <span className="text-xl lg:text-2xl font-extrabold tracking-tight text-yarbo-dark leading-none" data-id="element-132">
                Merem Robotics
              </span>
              <span className="hidden sm:block text-[10px] lg:text-xs font-medium text-gray-500 tracking-wide mt-0.5" data-id="element-133">
                Yarbo robotic yard-care systems
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-6" data-id="element-134">
              <div className="relative" onMouseEnter={() => setShopDropdownOpen(true)} onMouseLeave={() => setShopDropdownOpen(false)} data-id="element-135">
                <Link to="/shop" className="flex items-center text-gray-800 hover:text-yarbo-dark font-semibold text-sm py-2" data-id="element-136">
                  Shop All <ChevronDown className="ml-1 w-4 h-4" data-id="element-137" />
                </Link>

                <AnimatePresence data-id="element-138">
                  {shopDropdownOpen && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} transition={{
                  duration: 0.15
                }} className="absolute top-full left-0 w-64 bg-white shadow-xl rounded-lg border border-gray-100 py-2 mt-1" data-id="element-139">
                      {shopLinks.map(link => <Link key={link.name} to={link.path} className="block px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-yarbo-dark" data-id="element-140">
                          {link.name}
                        </Link>)}
                    </motion.div>}
                </AnimatePresence>
              </div>

              <Link to="/category/lawn-mowers" className="text-gray-800 hover:text-yarbo-dark font-semibold text-sm" data-id="element-141">
                Robot Lawn Mowers
              </Link>
              <Link to="/category/snow-blowers" className="text-gray-800 hover:text-yarbo-dark font-semibold text-sm" data-id="element-142">
                Robotic Snow Blowers
              </Link>
              <Link to="/category/bundles" className="text-gray-800 hover:text-yarbo-dark font-semibold text-sm" data-id="element-143">
                Bundles
              </Link>
              <Link to="/category/modules" className="text-gray-800 hover:text-yarbo-dark font-semibold text-sm" data-id="element-144">
                Modules &amp; Accessories
              </Link>
              <Link to="/compare" className="text-gray-800 hover:text-yarbo-dark font-semibold text-sm" data-id="element-145">
                Compare
              </Link>
              <Link to="/faq" className="text-gray-600 hover:text-yarbo-dark font-medium text-sm" data-id="element-146">
                FAQ
              </Link>
              <Link to="/contact" className="text-gray-600 hover:text-yarbo-dark font-medium text-sm" data-id="element-147">
                Contact
              </Link>
            </nav>

            {/* Right Utilities */}
            <div className="flex items-center space-x-1 sm:space-x-2" data-id="element-148">
              <button onClick={() => setSearchOpen(!searchOpen)} className="p-2 text-gray-700 hover:text-yarbo-dark hover:bg-gray-100 rounded-lg" aria-label="Search" data-id="element-149">
                <Search className="w-5 h-5" data-id="element-150" />
              </button>
              <button className="hidden sm:flex p-2 text-gray-700 hover:text-yarbo-dark hover:bg-gray-100 rounded-lg" aria-label="Account" data-id="element-151">
                <User className="w-5 h-5" data-id="element-152" />
              </button>
              <button onClick={openCart} className="relative p-2 text-gray-700 hover:text-yarbo-dark hover:bg-gray-100 rounded-lg" aria-label="Cart" data-id="element-153">
                <ShoppingCart className="w-5 h-5" data-id="element-154" />
                {itemCount > 0 && <motion.span initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} className="absolute -top-0.5 -right-0.5 bg-yarbo-yellow text-yarbo-dark text-[10px] font-extrabold w-5 h-5 rounded-full flex items-center justify-center" data-id="element-155">
                    {itemCount}
                  </motion.span>}
              </button>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 text-yarbo-dark hover:bg-gray-100 rounded-lg" aria-label="Menu" data-id="element-156">
                {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-157" /> : <Menu className="w-6 h-6" data-id="element-158" />}
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <AnimatePresence data-id="element-159">
            {searchOpen && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} className="overflow-hidden border-t border-gray-100" data-id="element-160">
                <div className="py-4 flex gap-2" data-id="element-161">
                  <input type="text" placeholder="Search Yarbo products..." className="flex-1 px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-yarbo-yellow focus:border-transparent outline-none" autoFocus data-id="element-162" />
                  <button className="bg-yarbo-dark text-white px-6 rounded-lg font-bold hover:bg-black" data-id="element-163">
                    Search
                  </button>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence data-id="element-164">
          {mobileMenuOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} className="lg:hidden bg-white border-t border-gray-100 overflow-hidden" data-id="element-165">
              <div className="px-4 py-4 space-y-1" data-id="element-166">
                {shopLinks.map(link => <Link key={link.name} to={link.path} className="block px-3 py-3 text-base font-semibold text-gray-800 hover:bg-gray-50 rounded-lg" data-id="element-167">
                    {link.name}
                  </Link>)}
                <div className="pt-3 mt-3 border-t border-gray-100" data-id="element-168">
                  <Link to="/compare" className="block px-3 py-3 text-base font-semibold text-gray-800 hover:bg-gray-50 rounded-lg" data-id="element-169">
                    Compare Systems
                  </Link>
                  <Link to="/faq" className="block px-3 py-3 text-base font-medium text-gray-600 hover:bg-gray-50 rounded-lg" data-id="element-170">
                    FAQ
                  </Link>
                  <Link to="/contact" className="block px-3 py-3 text-base font-medium text-gray-600 hover:bg-gray-50 rounded-lg" data-id="element-171">
                    Contact
                  </Link>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </header>
    </>;
}